import React, { useState, PropsWithChildren, useEffect, useContext, useCallback, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { io } from "socket.io-client";
import { fetchNotifications } from "hooks/notification";
import type { NotificationInterface } from "types/notification";
import { fetchAllConversations, threadKeys } from "hooks/thread";
import type { ThreadConversationInterface } from "types/thread";
import { AuthContext } from "context";

export enum NotificationComponentType {
    PAGE,
    POP_UP,
}

export interface NotificationProviderInterface {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    threads: ThreadConversationInterface[];
    refreshThreads: () => void;
    notifications: NotificationInterface[];
    refreshNotifications: () => void;
    unreadNotifications: NotificationInterface[];
    getNotificationComponentType: () => NotificationComponentType;
}

const BookingContext = React.createContext<NotificationProviderInterface>({
    isOpen: false,
    setIsOpen: () => [],
    threads: [],
    refreshThreads: () => {},
    notifications: [],
    refreshNotifications: () => {},
    unreadNotifications: [],
    getNotificationComponentType: () => NotificationComponentType.POP_UP,
});

const Consumer = BookingContext.Consumer;

const SOCKET_URL: string = process.env.REACT_APP_SOCKET_URL || "";

const socket = io(SOCKET_URL, {
    autoConnect: false,
    withCredentials: true,
});

const Provider: React.FC<PropsWithChildren<{}>> = ({ children }) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const { account } = useContext(AuthContext);
    const isLoggedIn = Boolean(account?.user.id);
    const queryClient = useQueryClient();
    const { data, refetch: refetchNotifications } = useQuery({
        queryKey: ["notifications"],
        queryFn: fetchNotifications,
        enabled: isLoggedIn,
    });
    const { data: dataThreads, refetch: refetchThreads } = useQuery({
        queryKey: ["all-conversations"],
        queryFn: fetchAllConversations,
        enabled: isLoggedIn,
    });
    const [unreadNotifications, setUnreadNotifications] = useState<NotificationInterface[]>([]);

    const refreshNotifications = useCallback(() => {
        void refetchNotifications();
    }, [refetchNotifications]);

    const refreshThreads = useCallback(() => {
        void refetchThreads();
        void queryClient.invalidateQueries({ queryKey: threadKeys.all });
    }, [refetchThreads, queryClient]);

    const refreshNotificationsRef = useRef(refreshNotifications);
    const refreshThreadsRef = useRef(refreshThreads);
    refreshNotificationsRef.current = refreshNotifications;
    refreshThreadsRef.current = refreshThreads;

    useEffect(() => {
        if (data) {
            let unread: NotificationInterface[] = [];

            data.forEach((notification) => {
                if (!notification.is_read) {
                    unread = [...unread, notification];
                }
            });

            setUnreadNotifications(unread);
        }
    }, [data, setUnreadNotifications, account]);

    useEffect(() => {
        if (account?.user.id) {
            void refetchNotifications();
            void refetchThreads();
        }
    }, [account?.user.id, refetchNotifications, refetchThreads]);

    useEffect(() => {
        const userId = account?.user.id;
        if (!userId) {
            return;
        }

        const handleConnect = () => {
            socket.emit("register_presence", { user_id: userId });
            socket.emit("join_room", userId);
            refreshThreadsRef.current();
        };

        const handleRefreshThreads = () => {
            refreshThreadsRef.current();
        };

        const handleNewNotification = () => {
            refreshNotificationsRef.current();
        };

        const handleVisibility = () => {
            if (document.visibilityState === "visible") {
                refreshThreadsRef.current();
            }
        };

        socket.on("connect", handleConnect);
        socket.on("refresh_threads", handleRefreshThreads);
        socket.on("new_notification", handleNewNotification);
        socket.connect();

        if (socket.connected) {
            handleConnect();
        }

        document.addEventListener("visibilitychange", handleVisibility);

        return () => {
            socket.off("connect", handleConnect);
            socket.off("refresh_threads", handleRefreshThreads);
            socket.off("new_notification", handleNewNotification);
            socket.disconnect();
            document.removeEventListener("visibilitychange", handleVisibility);
        };
    }, [account?.user.id]);

    const getNotificationComponentType = () => {
        if (window.innerWidth < 1024) {
            return NotificationComponentType.PAGE;
        }

        return NotificationComponentType.POP_UP;
    };

    return (
        <BookingContext.Provider
            value={{
                isOpen,
                setIsOpen,
                threads: dataThreads ?? [],
                refreshThreads,
                notifications: data ?? [],
                refreshNotifications,
                unreadNotifications,
                getNotificationComponentType,
            }}
        >
            {children}
        </BookingContext.Provider>
    );
};

export { Provider, Consumer, BookingContext as Context };
